// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, MapPin, Eye, Car, ChevronRight, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import { VCIN_COLORS } from '../../styles/constants';

// Loading Skeleton for stat cards
function StatCardSkeleton() {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
      <div className="flex items-center justify-between">
        <div>
          <div className="h-4 w-24 bg-gray-200 rounded mb-3" />
          <div className="h-8 w-16 bg-gray-200 rounded" />
        </div>
        <div className="w-12 h-12 bg-gray-200 rounded-xl" />
      </div>
    </div>
  );
}

// Loading Skeleton for table rows
function TableRowSkeleton() {
  return (
    <tr className="animate-pulse">
      <td className="px-6 py-4"><div className="h-4 w-28 bg-gray-200 rounded" /></td>
      <td className="px-6 py-4"><div className="h-6 w-20 bg-gray-200 rounded" /></td>
      <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
      <td className="px-6 py-4"><div className="h-5 w-16 bg-gray-200 rounded" /></td>
      <td className="px-6 py-4"><div className="h-4 w-28 bg-gray-200 rounded" /></td>
      <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
      <td className="px-6 py-4"><div className="h-4 w-20 bg-gray-200 rounded" /></td>
    </tr>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, color, onClick }) {
  return (
    <div 
      className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      onClick={onClick}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm font-medium mb-1">{label}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
    </div>
  );
}

// Threat Level Badge
function ThreatBadge({ level }) {
  const colors = {
    0: { bg: '#E8F5E9', text: '#2E7D32' },
    1: { bg: '#FFF8E1', text: '#F57F17' },
    2: { bg: '#FFF3E0', text: '#E65100' },
    3: { bg: '#FFEBEE', text: '#C62828' },
    4: { bg: '#FFCDD2', text: '#B71C1C' },
    5: { bg: '#B71C1C', text: '#FFFFFF' }
  };
  const config = colors[level] || colors[0];
  
  return (
    <span 
      className="px-2 py-1 rounded text-xs font-semibold"
      style={{ backgroundColor: config.bg, color: config.text }}
    >
      Level {level}
    </span>
  );
}

// Empty State Component
function EmptyState({ icon: Icon, title, description, actionLabel, onAction }) {
  return (
    <div className="p-12 text-center">
      <Icon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      <p className="font-medium text-gray-600">{title}</p>
      <p className="text-sm text-gray-400 mt-1 mb-4">{description}</p>
      {actionLabel && onAction && (
        <button
          onClick={onAction}
          className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
          style={{ backgroundColor: VCIN_COLORS.primary }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentSightings, setRecentSightings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [statsRes, sightingsRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/dashboard/recent-sightings')
        ]);
        setStats(statsRes.data.stats);
        setRecentSightings(sightingsRes.data.sightings || []);
      } catch (err) {
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <span className="text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {loading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              icon={Users}
              label="Total Officers"
              value={stats?.total_officers || 0}
              color={VCIN_COLORS.primary}
              onClick={() => navigate('/admin/officers')}
            />
            <StatCard
              icon={MapPin}
              label="Active Sites"
              value={stats?.active_sites || 0}
              color="#059669"
              onClick={() => navigate('/admin/sites')}
            />
            <StatCard
              icon={Eye}
              label="Sightings This Month"
              value={stats?.sightings_this_month || 0}
              color="#D97706"
              onClick={() => navigate('/admin/sightings')}
            />
            <StatCard
              icon={Car}
              label="Vehicles on Watch"
              value={stats?.vehicles_on_watch || 0}
              color="#DC2626"
              onClick={() => navigate('/admin/known-vehicles')}
            />
          </>
        )}
      </div>

      {/* Recent Sightings Table */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Recent Sightings</h2>
          <button
            onClick={() => navigate('/admin/sightings')}
            className="text-sm font-medium flex items-center gap-1 hover:underline"
            style={{ color: VCIN_COLORS.primary }}
          >
            View All
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">VCIN Ref</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">VRN</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Threat Level</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Officer</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Site</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[...Array(5)].map((_, i) => <TableRowSkeleton key={i} />)}
              </tbody>
            </table>
          </div>
        ) : recentSightings.length === 0 ? (
          <EmptyState
            icon={Eye}
            title="No sightings recorded yet"
            description="Sightings will appear here once officers submit them"
            actionLabel="View All Sightings"
            onAction={() => navigate('/admin/sightings')}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="recent-sightings-table">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    VCIN Ref
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    VRN
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Threat Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Officer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Site
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentSightings.map((sighting) => (
                  <tr 
                    key={sighting.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/sightings?id=${sighting.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span 
                        className="font-mono text-sm font-medium"
                        style={{ color: VCIN_COLORS.primary }}
                      >
                        {sighting.vcin_reference}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span 
                        className="px-2 py-1 rounded text-sm font-bold"
                        style={{ 
                          backgroundColor: VCIN_COLORS.plateYellow,
                          fontFamily: 'monospace'
                        }}
                      >
                        {sighting.vrn}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {sighting.primary_category?.replace(/_/g, ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <ThreatBadge level={sighting.threat_level} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {sighting.officer_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {sighting.site_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(sighting.datetime).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
